import React from 'react';
import { mergeProps, useButton, useFocusRing } from 'react-aria';
import { IconButtonProps } from './icon-button.types';
import * as S from './icon-button.styles';
import { Icon } from '@design-system/atoms/icon/Icon';
import { useIconButtonIconColor } from './hooks';

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      icon: IconProp,
      size = 24,
      isDisabled = false,
      className,
      onPress,
      title,
      'aria-label': ariaLabel,
      style,
      variant = 'default',
      tone = 'default',
      ...props
    },
    ref
  ) => {
    // Use React Aria's useButton hook for accessibility
    const { buttonProps } = useButton(
      {
        isDisabled,
        onPress,
        'aria-label': ariaLabel || title,
      },
      ref as React.RefObject<HTMLButtonElement>
    );

    // Use React Aria's useFocusRing hook for focus state
    const { isFocusVisible, focusProps } = useFocusRing();

    // Get icon color based on tone
    const iconColor = useIconButtonIconColor(tone, isDisabled);

    return (
      <S.IconButtonContainer
        ref={ref}
        isFocusVisible={isFocusVisible}
        isDisabled={isDisabled}
        $size={size}
        className={className}
        disabled={isDisabled}
        title={title}
        style={style}
        $variant={variant}
        $tone={tone}
        {...mergeProps(props, buttonProps, focusProps)}
        aria-disabled={isDisabled}
      >
        {typeof IconProp === 'string' ? (
          <Icon name={IconProp} size={size} color={iconColor} />
        ) : (
          <IconProp size={size} color={iconColor} />
        )}
      </S.IconButtonContainer>
    );
  }
);
